
import { jwtDecode } from "jwt-decode";
import { createContext, useContext, useEffect, useState } from "react";
import { authRepository, Register, Signin, User } from "../api/authRepository";
import axiosInstance from "api/axiosinstance";

interface AuthContextType {
  token: string | null;
  userId: string | null;
  email: string | null;
  fullName: string | null;
  profilePictureUrl: string | null;
  setProfilePictureUrl: (url: string | null) => void;
  login: (login: Signin) => Promise<void>;
  signup: (signup: Register) => Promise<void>;
  user: User | null;
  logout: () => void;
  role: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
const EMAIL_CLAIM = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem("jwt"));
  const [userId, setUserId] = useState<string | null>(() => localStorage.getItem("userId"));
  const [email, setEmail] = useState<string | null>(() => localStorage.getItem("email"));
  const [role, setRole] = useState<string | null>(() => localStorage.getItem("role"));
  const [fullName, setFullName] = useState<string | null>(() => localStorage.getItem("fullName"));
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);

  const login = async (login: Signin) => {
    const { token: newToken, refreshToken } = await authRepository.Login(login);
    const decoded: any = jwtDecode(newToken);

    const tokenUserId = decoded["id"] ?? null;
    const tokenEmail = decoded["email"] || decoded[EMAIL_CLAIM] || login.email;
    const tokenRole = decoded[ROLE_CLAIM] ?? null;

    localStorage.setItem("jwt", newToken);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("email", tokenEmail || "");
    localStorage.setItem("fullName", decoded.fullName ?? "");
    if (tokenUserId) localStorage.setItem("userId", tokenUserId);
    if (tokenRole) localStorage.setItem("role", tokenRole);

    setToken(newToken);
    setUserId(tokenUserId);
    setEmail(tokenEmail || null);
    setFullName(decoded.fullName ?? null);
    setRole(tokenRole);
  };

  const signup = async (signup: Register): Promise<void> => {
    try {
      // was posting `user` (always null) before; now posts the form data
      const response = await axiosInstance.post("/api/auth/register", signup);
      console.log("Signup Success:", response.data);
      return response.data;
    } catch (error) {
      console.error("Signup Error:", error);
      throw error;
    }
  };

  const logout = () => {
    authRepository.logout();

    setToken(null);
    setUserId(null);
    setEmail(null);
    setRole(null);
    setFullName(null);
    setUser(null);
    setProfilePictureUrl(null);
  };


  useEffect(() => {
    if (!token) return;

    try {
      const decoded: any = jwtDecode(token);
      const tokenEmail = decoded["email"] || decoded[EMAIL_CLAIM] || localStorage.getItem("email");
      const tokenUserId = decoded["id"] ?? null;
      const tokenRole = decoded["role"] || decoded[ROLE_CLAIM] || null;
      setFullName(decoded["fullName"] ?? null);
      setEmail(tokenEmail ?? null);
      setRole(tokenRole);
      setUserId(tokenUserId);

      localStorage.setItem("fullName", decoded["fullName"] ?? "");

      if (tokenUserId) localStorage.setItem("userId", tokenUserId);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(
    () => () => {
      if (profilePictureUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(profilePictureUrl);
      }
    },
    [profilePictureUrl]
  );

  return (
    <AuthContext.Provider
      value={{
        token,
        userId,
        role,
        email,
        fullName,
        profilePictureUrl,
        setProfilePictureUrl,
        login,
        signup,
        logout,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};